import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import { isNone } from '@ember/utils';
import ApiErrorsService from 'cio/services/api-errors';
import EnvironmentService from 'cio/services/environment';
import ImportModel, { ImportState, UPDATE_TYPES } from 'cio/models/import';
import { importStateGte } from 'cio/helpers/import-state-gte';
import { ImportAttribute, filterSamples } from 'cio/utils/imports/import-attribute';
import { generateValidateRules } from 'cio/utils/imports/generate-validate-rules';
import AnalyticsService from 'cio/services/analytics';
import { next } from '@ember/runloop';
import AlertsService from 'cio/services/alerts';
import AccountService from 'cio/services/account';
import { CREATED_IN_CIO_AT, RESTRICTED_ATTRIBUTES } from 'cio/utils/attributes';
import AuthService from 'cio/services/auth';
import joinWords from 'cio/utils/join-words';
import RouterService from '@ember/routing/router-service';
import { IdentifierKey, ObjectIdentifierKey, ObjectTypeIdentifierKey } from 'cio/utils/identifiers';

export default class AttributesController extends Controller {
  @service apiErrors!: ApiErrorsService;
  @service environment!: EnvironmentService;
  @service analytics!: AnalyticsService;
  @service alerts!: AlertsService;
  @service account!: AccountService;
  @service auth!: AuthService;
  @service router!: RouterService;

  declare model: ImportModel;

  attributes: Record<string, any>[] = [];
  allRestrictedAttributes = RESTRICTED_ATTRIBUTES;
  isShowingMigrateModal = false;

  @computed('model', 'model.hasDirtyAttributes')
  get isModelDirty() {
    if (isNone(this.model)) {
      return false;
    }

    let changedAttributes = Object.keys(this.model.changedAttributes()).filter((attribute) => {
      return attribute !== 'updateType';
    });

    return this.model.get('hasDirtyAttributes') && changedAttributes.length > 0;
  }

  @computed('model', 'model.isDeleted', 'isModelDirty', 'attributes.@each.isDirty')
  get isDirty() {
    return !this.model.isDeleted && (this.isModelDirty || this.attributes.some((attribute) => attribute.isDirty));
  }

  @computed('attributes.@each.{to,isRequired,skip}')
  get hasPrimaryAttributeMapping() {
    let primaryAttribute = this.attributes.find((attr) => attr.isRequired && !attr.skip && attr.to);

    return !!primaryAttribute;
  }

  @computed('model.identifier', 'mappings.[]')
  get shouldNotHaveCioField() {
    return this.model.identifier !== IdentifierKey.CioId && this.mappings.includes(IdentifierKey.CioId);
  }

  @computed('model.{identifier,validateRules.onlyExisting}')
  get alternateIdentifiers() {
    let allIdentifiers = this.environment.current.enabledIdentifiers;

    if (this.model.validateRules.onlyExisting && this.environment.current.isMultiIdentifier) {
      allIdentifiers = this.environment.current.allEnabledIdentifiers;
    }

    let otherAvailableIdentifiers = allIdentifiers.filter((identifier) => identifier !== this.model.identifier);

    if (otherAvailableIdentifiers.length) {
      return joinWords(otherAvailableIdentifiers, ', ', ' or ');
    }

    return;
  }

  @computed('mappings.[]', 'model.{identifier,validateRules.onlyExisting}')
  get alternateIdentifierMappings() {
    let allIdentifiers = this.environment.current.enabledIdentifiers;

    if (this.model.validateRules.onlyExisting && this.environment.current.isMultiIdentifier) {
      allIdentifiers = this.environment.current.allEnabledIdentifiers;
    }

    let otherAvailableIdentifiers = allIdentifiers.filter((identifier) => identifier !== this.model.identifier);
    let mappedIdentifiers = this.mappings.filter((mapping) => otherAvailableIdentifiers.includes(mapping));

    if (mappedIdentifiers.length) {
      return joinWords(mappedIdentifiers);
    }

    return;
  }

  @computed('model.identifier')
  get primaryAttributeMissingText() {
    return `Mapping ${this.model.identifier} is required before moving on`;
  }

  @computed('attributes.@each.{to,skip}')
  get mappings() {
    return this.attributes
      .filter((attribute) => !attribute.skip)
      .mapBy('to')
      .compact();
  }

  @computed('mappings')
  get hasDuplicateMappings() {
    return new Set(this.mappings).size !== this.mappings.length;
  }

  @computed('attributes.@each.{to,skip}')
  get hasEmptyMappings() {
    return this.attributes.filter((attribute) => !attribute.to && !attribute.skip).length;
  }

  get hasSkippedRequiredMappings() {
    let requiredMappings: string[] = [this.model.identifier];
    if (this.model.isObjectsImport) {
      requiredMappings = [ObjectIdentifierKey.ObjectId];
    } else if (this.model.isRelationshipsImport) {
      requiredMappings = [this.model.identifier, ObjectTypeIdentifierKey.ObjectTypeId, ObjectIdentifierKey.ObjectId];
    }
    return requiredMappings.some((required) => !this.mappings.includes(required));
  }

  @computed('mappings', 'model.validateRules.noId')
  get hasInvalidMappings() {
    if (this.model.validateRules.noId && this.mappings.includes('id')) {
      return true;
    }

    return false;
  }

  get disabledTooltipMessage() {
    if (this.model.isExpired) {
      return 'The imported CSV is no longer available. Re-upload your CSV to continue.';
    } else if (!this.hasPrimaryAttributeMapping) {
      return this.primaryAttributeMissingText;
    } else if (this.hasDuplicateMappings) {
      return 'Mapping each column to one attribute is required before continuing.';
    } else if (this.hasEmptyMappings) {
      return "Attribute names can't be empty.";
    } else if (this.hasSkippedRequiredMappings) {
      return 'All required fields must be mapped.';
    }

    return;
  }

  @computed('environment.current.enabledIdentifiers')
  get canMigrateEnvironment() {
    return !this.environment.current.isMultiIdentifier || this.environment.current.enabledIdentifiers.length === 1;
  }

  @computed(
    'model.hasDirtyAttributes',
    'hasDuplicateMappings',
    'hasEmptyMappings',
    'hasInvalidMappings',
    'hasPrimaryAttributeMapping',
    'startValidating.isRunning',
    'hasSkippedRequiredMappings',
  )
  get disableNextButton() {
    return (
      this.hasDuplicateMappings ||
      this.hasEmptyMappings ||
      this.hasInvalidMappings ||
      this.model.hasDirtyAttributes ||
      this.startValidating.isRunning ||
      !this.hasPrimaryAttributeMapping ||
      this.shouldNotHaveCioField ||
      this.hasSkippedRequiredMappings
    );
  }

  @action
  setupAttributes() {
    if (isNone(this.model)) {
      throw new Error('Cannot set up attributes because the model is not set');
    }

    this.set('attributes', this.preprocessedAttributes());
  }

  @action
  preprocessedAttributes() {
    let { validateMeta, validateRules, preprocessMeta } = this.model as ImportModel;
    let { mapAttributes, skipAttributes, noId } = validateRules;
    let { attributes, samples } = preprocessMeta;

    let { columnWarnings } = validateMeta ?? {};

    return attributes
      .filter((attribute) => attribute !== CREATED_IN_CIO_AT)
      .map((attribute, index) => {
        let to = mapAttributes[index];
        let skip = skipAttributes[index] ?? false;

        return ImportAttribute.create<any, Record<string, any>>({
          to,
          skip,
          primaryAttribute: (this.model as ImportModel).identifier,
          key: attribute,
          samples: filterSamples(samples, index),
          noId,
          initialWarnings: columnWarnings ? columnWarnings[attribute] || [] : [],
          restricted: RESTRICTED_ATTRIBUTES.includes(to ?? ''),
          preprocessedIndex: index,
          sentAttributes: this.environment.current.sentAttributes,
        });
      });
  }

  @action
  reset() {
    this.model.rollbackAttributes();
    this.setupAttributes();
  }

  @action
  onSkipping(attribute: Record<string, any>) {
    this.analytics.track('import', 'attribute_skipped', {
      model: this.model,
      attribute,
    });
  }

  @action
  onMapping(attribute: Record<string, any>) {
    this.analytics.track('import', 'attribute_mapped', {
      model: this.model,
      attribute,
    });
  }

  @action
  didDisplayWarning(element: HTMLDivElement) {
    // For some reason, innerText is empty until the next tick
    next(this, () => {
      this.analytics.track('import', 'field_warning_displayed', {
        model: this.model,
        warning: element.innerText.replace(/\n+/g, '. '),
      });
    });
  }

  @action
  toggleMigrationModal() {
    this.set('isShowingMigrateModal', !this.isShowingMigrateModal);
  }

  resetState = dropTask(async () => {
    if (importStateGte([this.model.state, ImportState.Validating])) {
      this.model.set('updateType', UPDATE_TYPES.reset_preprocessed);
      await this.model.save();
    }
  });

  updateValidateRules = dropTask(async () => {
    this.model.validateRules = {
      ...this.model.validateRules,
      ...generateValidateRules(this.model, this.attributes),
    };
    this.model.updateType = UPDATE_TYPES.validate_rules;

    await this.model.save();
  });

  startValidating = dropTask(async () => {
    try {
      if (!importStateGte([this.model.state, ImportState.Validating])) {
        await this.updateValidateRules.perform();
        await this.setUpdateType();
        this.attributes.forEach((attribute) => attribute.commit());
        this.goToValidationStep();
      }
    } catch (e) {
      this.apiErrors.report(e);
    }
  });

  private setUpdateType() {
    this.model.set('updateType', UPDATE_TYPES.validate);
    return this.model.save();
  }

  private goToValidationStep() {
    this.router.transitionTo('imports.import.setup.validating', this.model.id);
  }

  saveAndStartValidating = dropTask(async () => {
    await this.resetState.perform();
    await this.startValidating.perform();
  });
}
